import type {
  AuthState,
  TaskWithRelations,
  CreateTaskInput,
  UpdateTaskInput,
  TaskStatus,
  Priority,
  Project,
  Tag,
  TaskFilter,
  TaskSort,
  ViewKind,
  GroupableField,
} from '@shared/types';

/** A saved view, with its filter and sort already parsed by the server. */
export interface SavedView {
  id: string;
  name: string;
  kind: ViewKind;
  filter: TaskFilter;
  sorts: TaskSort[];
  group_by: GroupableField | null;
  position: number;
}

/** One card's landing spot after a drag: where it sits, and which column it is in. */
export interface ReorderMove {
  id: string;
  position: number;
  status?: TaskStatus;
  project_id?: string | null;
}

/** Patch applied to every selected task. Tags are additive, not a replace. */
export interface BulkPatch {
  status?: TaskStatus;
  priority?: Priority;
  project_id?: string | null;
  due_at?: string | null;
  add_tag_names?: string[];
  remove_tag_ids?: string[];
  archived?: boolean;
}

/** A project as the list endpoint returns it, with its open-task rollup. */
export interface ProjectWithCounts extends Project {
  open_count: number;
  total_count: number;
}

export interface TagWithCount extends Tag {
  task_count: number;
}

/**
 * Thrown for any non-2xx response. Carries the status so callers can branch on
 * 401 (session gone) without string-matching messages.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    detail?: string,
  ) {
    super(detail ?? code);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    // The session cookie is httpOnly; it only travels if credentials are sent.
    credentials: 'same-origin',
    headers: init.body ? { 'Content-Type': 'application/json' } : {},
    ...init,
  });

  if (res.status === 204) return undefined as T;

  const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };

  if (!res.ok) throw new ApiError(res.status, body.error ?? 'request_failed', body.detail);

  return body as T;
}

/** Turns a TaskFilter into the query string the tasks endpoint expects. */
export function filterToQuery(filter: TaskFilter, sorts?: TaskSort[]): string {
  const params = new URLSearchParams();

  if (filter.status?.length) params.set('status', filter.status.join(','));
  if (filter.priority?.length) params.set('priority', filter.priority.join(','));
  if (filter.project_id?.length) {
    // `none` is how the unassigned bucket is expressed; an empty value cannot.
    params.set('project', filter.project_id.map((p) => p ?? 'none').join(','));
  }
  if (filter.tag_ids?.length) params.set('tag', filter.tag_ids.join(','));
  if (filter.tags_match_all) params.set('tags_match_all', 'true');
  if (filter.due_before) params.set('due_before', filter.due_before);
  if (filter.due_after) params.set('due_after', filter.due_after);
  if (filter.no_due_date) params.set('no_due_date', 'true');
  if (filter.updated_before) params.set('updated_before', filter.updated_before);
  if (filter.completed_after) params.set('completed_after', filter.completed_after);
  if (filter.q) params.set('q', filter.q);
  if (filter.top_level_only) params.set('top_level', 'true');
  if (filter.include_archived) params.set('include_archived', 'true');
  if (sorts?.length) params.set('sort', sorts.map((s) => `${s.field}:${s.dir}`).join(','));

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  auth: {
    me: () => request<AuthState>('/auth/me'),
    login: (password: string) =>
      request<AuthState>('/auth/login', { method: 'POST', body: JSON.stringify({ password }) }),
    setup: (password: string) =>
      request<AuthState>('/auth/setup', { method: 'POST', body: JSON.stringify({ password }) }),
    logout: () => request<AuthState>('/auth/logout', { method: 'POST' }),
  },

  tasks: {
    list: (filter: TaskFilter = {}, sorts?: TaskSort[]) =>
      request<{ tasks: TaskWithRelations[] }>(`/tasks${filterToQuery(filter, sorts)}`).then(
        (r) => r.tasks,
      ),

    get: (id: string) => request<{ task: TaskWithRelations }>(`/tasks/${id}`).then((r) => r.task),

    subtasks: (id: string) =>
      request<{ tasks: TaskWithRelations[] }>(`/tasks/${id}/subtasks`).then((r) => r.tasks),

    create: (input: CreateTaskInput) =>
      request<{ task: TaskWithRelations }>('/tasks', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.task),

    update: (id: string, input: UpdateTaskInput) =>
      request<{ task: TaskWithRelations }>(`/tasks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }).then((r) => r.task),

    setStatus: (id: string, status: TaskStatus) =>
      request<{ task: TaskWithRelations }>(`/tasks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }).then((r) => r.task),

    remove: (id: string) => request<void>(`/tasks/${id}`, { method: 'DELETE' }),

    bulkUpdate: (ids: string[], patch: BulkPatch) =>
      request<{ tasks: TaskWithRelations[] }>('/tasks/bulk', {
        method: 'PATCH',
        body: JSON.stringify({ ids, patch }),
      }).then((r) => r.tasks),

    bulkDelete: (ids: string[]) =>
      request<{ deleted: number }>('/tasks/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }).then((r) => r.deleted),

    reorder: (moves: ReorderMove[]) =>
      request<{ tasks: TaskWithRelations[] }>('/tasks/reorder', {
        method: 'POST',
        body: JSON.stringify({ moves }),
      }).then((r) => r.tasks),
  },

  views: {
    list: () => request<{ views: SavedView[] }>('/views').then((r) => r.views),

    get: (id: string) => request<{ view: SavedView }>(`/views/${id}`).then((r) => r.view),

    create: (input: {
      name: string;
      kind: ViewKind;
      filter?: TaskFilter;
      sorts?: TaskSort[];
      group_by?: GroupableField | null;
    }) =>
      request<{ view: SavedView }>('/views', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.view),

    update: (
      id: string,
      input: Partial<{
        name: string;
        filter: TaskFilter;
        sorts: TaskSort[];
        group_by: GroupableField | null;
      }>,
    ) =>
      request<{ view: SavedView }>(`/views/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }).then((r) => r.view),

    remove: (id: string) => request<void>(`/views/${id}`, { method: 'DELETE' }),
  },

  projects: {
    list: () => request<{ projects: ProjectWithCounts[] }>('/projects').then((r) => r.projects),

    create: (name: string, color?: string) =>
      request<{ project: Project }>('/projects', {
        method: 'POST',
        body: JSON.stringify({ name, ...(color ? { color } : {}) }),
      }).then((r) => r.project),

    update: (id: string, input: Partial<{ name: string; color: string; archived: boolean }>) =>
      request<{ project: Project }>(`/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }).then((r) => r.project),

    remove: (id: string) => request<void>(`/projects/${id}`, { method: 'DELETE' }),
  },

  tags: {
    list: () => request<{ tags: TagWithCount[] }>('/tags').then((r) => r.tags),

    create: (name: string) =>
      request<{ tag: Tag }>('/tags', { method: 'POST', body: JSON.stringify({ name }) }).then(
        (r) => r.tag,
      ),

    update: (id: string, input: Partial<{ name: string; color: string }>) =>
      request<{ tag: Tag }>(`/tags/${id}`, { method: 'PATCH', body: JSON.stringify(input) }).then(
        (r) => r.tag,
      ),

    remove: (id: string) => request<void>(`/tags/${id}`, { method: 'DELETE' }),
  },
};
